import React, { Fragment, lazy } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import "./index.scss";
import reportWebVitals from "./reportWebVitals";
import NetworkError from "./errors/NetworkError";
import Unauthorized from "./errors/Unauthorized";
import NotFound from "./errors/NotFound";
import BadRequest from "./errors/BadRequest";
import { ErrorBoundary } from "react-error-boundary";
import AppProviders from "./providers/ContextProviders"; //All Context Provider File
import ProtectedRoute from "./ProtectedRoute";

const App = lazy(() => import("./components/app"));
const AuthLogin = lazy(() =>
  import("./components/Authentication/firebaseAuth/AuthLogin")
);
const Auth = lazy(() =>
  import("./components/Authentication/firebaseAuth/auth")
);
const Loader = lazy(() => import("./Layouts/Loader/Loader"));
const ForgotPassword = lazy(() =>
  import("./components/Authentication/Forgot Password/ForgotPassword")
);
const ResetPassword = lazy(() =>
  import("./components/Authentication/ResetPassword/ResetPassword")
);
const TokenExpired = lazy(() =>
  import("./components/Authentication/TokenExpired/TokenExpired")
);
const AlreadyActive = lazy(() =>
  import("./components/Authentication/TokenExpired/AlreadyActive")
);
const DIYAccounts = lazy(() =>
  import("./components/Authentication/TokenExpired/DIYAccounts")
);

const CreativeSpecs = lazy(() =>
  import("./components/Authentication/TokenExpired/CreativeSpecs")
);

const UserCreation = lazy(() =>
  import("./components/Authentication/UserCreation/UserCreation")
);
const PublisherLogin = lazy(() =>
  import("./components/Authentication/PublisherLogin/PublisherLogin")
);

//Site
const SiteCreate = lazy(() => import("./components/Site/ParentSiteCreate"));
const SitesView = lazy(() => import("./components/Site/ParentSiteView"));

//Brand
const BrandCreate = lazy(() => import("./components/Brand/ParentBrandCreate"));
const BrandView = lazy(() => import("./components/Brand/ParentBrandView"));

//Creative Approval
const CreativeAprroval = lazy(() =>
  import("./components/CreativeApproval/CreativeApproval")
);

// Site Approval
const SiteAprroval = lazy(() =>
  import("./components/SiteApproval/SiteApproval")
);

//Wallet
const WalletDIYAccount = lazy(() =>
  import("./components/Wallet/DiyAccount/Wallet")
);
const WalletAdminControls = lazy(() =>
  import("./components/Wallet/AdminControls/Wallet")
);
const GlobalConfigFlags = lazy(() =>
  import("./components/Flag/GlobalConfigFlags")
);

//Dashboard
const DashboardRoute = lazy(() =>
  import("./components/Dashboard/DashboardRoute")
);
const ImpersonateRoute = lazy(() =>
  import("./components/Impersonate/ImpersonateRoute")
);

//Campaign
const CampaignCreate = lazy(() =>
  import("./components/Campaign/Create/CampaignCreate")
);
const CampaignView = lazy(() =>
  import("./components/Campaign/View/CampaignView")
);

// Partner

const PartnerCreateRoute = lazy(() =>
  import("./components/Partner/PartnerCreateRoute")
);

const PartnerViewRoute = lazy(() =>
  import("./components/Partner/PartnerViewRoute")
);

const AccountCreate = lazy(() =>
  import("./components/Account/AccountCreate/AccountCreate")
);
const AccountView = lazy(() =>
  import("./components/Account/AccountView/AccountView")
);

// Creatives
const CreativesCreate = lazy(() =>
  import("./components/Creatives/Create/CreativesCreate")
);
const CreativesView = lazy(() =>
  import("./components/Creatives/View/CreativesView")
);

//Translations
const Translations = lazy(() =>
  import("./components/Translation/Translations")
);

const Translatorcreate = lazy(() =>
  import("./components/Translator/Translatorcreate")
);

//Report
const Report = lazy(() => import("./components/Reports/Report"));
const ReportComponent = lazy(() => import("reportApp/ReportComponent"));

//Error Page
const Error400 = lazy(() =>
  import("./components/Authentication/errorPage/Error400/Error400")
);
const Error500 = lazy(() =>
  import("./components/Authentication/errorPage/Error500/Error500")
);
const ErrorPages = lazy(() => import("./components/ErrorPages"));

const Error403 = lazy(() =>
  import("./components/Authentication/errorPage/Error403/Error403")
);
const Error404 = lazy(() =>
  import("./components/Authentication/errorPage/Error404/Error404")
);
const Error503 = lazy(() =>
  import("./components/Authentication/errorPage/Error503/Error503")
);

const Error401 = lazy(() =>
  import("./components/Authentication/errorPage/Error401/Error401")
);

//Ocean OnBoarding

const OceanOnBoardingCreate = lazy(() =>
  import(
    "./components/OceanOnBoarding/OceanOnBoardingCreate/OceanOnBoardingCreate"
  )
);
const OceanOnBoardingView = lazy(() =>
  import("./components/OceanOnBoarding/OceanOnBoardingView/OceanOnBoardingView")
);

// Template - IBN Ocean
const Template = lazy(() => import("./components/Templates/Template"));
const TemplateDetails = lazy(() =>
  import("./components/Templates/TemplateDetails")
);

// Wallpaper
const Wallpaper = lazy(() => import("./components/Wallpaper/Wallpaper"));

// FAQ's
const FAQ = lazy(() => import("./components/FAQS/FAQ"));
const FAQCreate = lazy(() => import("./components/FAQS/FAQCreate"));
const FAQView = lazy(() => import("./components/FAQS/FAQView"));

//Manage User
const ManageUserCreate = lazy(() =>
  import("./components/ManageUser/CreateUser/CreateUser")
);
const ManageUserView = lazy(() =>
  import("./components/ManageUser/ViewUser/ViewUserParent")
);

const customError = ({ error, resetErrorBoundary }) => {
  if (error instanceof BadRequest) {
    return <Error400 resetErrorBoundary={resetErrorBoundary} />;
  } else if (error instanceof NetworkError) {
    return <Error500 resetErrorBoundary={resetErrorBoundary} />;
  } else if (error instanceof Unauthorized) {
    console.log("Unauthorized");
    return <Error401 resetErrorBoundary={resetErrorBoundary} />;
  } else if (error instanceof NotFound) {
    return <Error404 resetErrorBoundary={resetErrorBoundary} />;
  } else {
    return <Error500 resetErrorBoundary={resetErrorBoundary} />;
  }
};

const logError = (error, info) => {
  // Do something with the error, e.g. log to an external API
  console.error(error);
  console.error(error.message);
  console.info(info.componentStack);
};

const container = document.getElementById("root");
const root = createRoot(container);

root.render(
  <Fragment>
    <BrowserRouter>
      <AppProviders>
        <ErrorBoundary FallbackComponent={customError} onError={logError}>
          <React.Suspense fallback={<Loader />}>
            <Routes>
              {/* Authentication */}
              <Route path={`${process.env.PUBLIC_URL}/`} element={<Auth />}>
                <Route index element={<AuthLogin />} />
                <Route
                  path={`${process.env.PUBLIC_URL}/authentication/firebaseAuth/login`}
                  element={<AuthLogin />}
                />
                <Route
                  path={`${process.env.PUBLIC_URL}/authentication/forgotpassword`}
                  element={<ForgotPassword />}
                />
                <Route
                  path={`${process.env.PUBLIC_URL}/authentication/resetpassword`}
                  element={<ResetPassword />}
                />
                <Route
                  path={`${process.env.PUBLIC_URL}/authentication/tokenexpired`}
                  element={<TokenExpired />}
                />
                <Route
                  path={`${process.env.PUBLIC_URL}/authentication/alreadyactive`}
                  element={<AlreadyActive />}
                />
                <Route
                  path={`${process.env.PUBLIC_URL}/authentication/diyaccounts`}
                  element={<DIYAccounts />}
                />
                <Route
                  path={`${process.env.PUBLIC_URL}/authentication/creativespecs`}
                  element={<CreativeSpecs />}
                />
                <Route
                  path={`${process.env.PUBLIC_URL}/authentication/usercreation`}
                  element={<UserCreation />}
                />
              </Route>

              {/* Internal Routes */}
              <Route
                path={`${process.env.PUBLIC_URL}/`}
                element={<ProtectedRoute element={<App />} />}
              >
                {/* Publisher Signup */}
                <Route
                  path={`${process.env.PUBLIC_URL}/authentication/publisherlogin/`}
                  element={<PublisherLogin />}
                />
                <Route>
                  <Route
                    path={`reports/*`}
                    // element={<Report />}
                    element={<ReportComponent />}
                  />
                </Route>
                <Route>
                  <Route
                    path={`reports/*`}
                    element={<Report />}
                  />
                </Route>
                {/* <Route>
                  <Route path={`${process.env.PUBLIC_URL}/components/reports/*`} element={<ReportComponent />} />
                </Route> */}

                {/* Brand */}
                <Route>
                  <Route
                    path={`${process.env.PUBLIC_URL}/components/Brand/parentbrandcreate`}
                    element={<BrandCreate />}
                  />
                  <Route
                    path={`${process.env.PUBLIC_URL}/components/Brand/parentbrandview:accountId?`}
                    element={<BrandView />}
                  />
                </Route>

                {/* Site 2*/}
                <Route>
                  <Route
                    path={`${process.env.PUBLIC_URL}/components/site/parentsitecreate`}
                    element={<SiteCreate />}
                  />
                  <Route
                    path={`${process.env.PUBLIC_URL}/components/site/parentsiteview`}
                    element={<SitesView />}
                  />
                </Route>

                {/* Creative and Site Approval */}
                <Route>
                  <Route
                    path={`${process.env.PUBLIC_URL}/components/creativeapproval/creativeapproval`}
                    element={<CreativeAprroval />}
                  />
                  <Route
                    path={`${process.env.PUBLIC_URL}/components/siteapproval/siteapproval`}
                    element={<SiteAprroval />}
                  />
                </Route>

                {/* Wallet */}
                <Route>
                  <Route
                    path={`${process.env.PUBLIC_URL}/components/wallet/diyaccount/wallet`}
                    element={<WalletDIYAccount />}
                  />
                  <Route
                    path={`${process.env.PUBLIC_URL}/components/wallet/admincontrolls/wallet`}
                    element={<WalletAdminControls />}
                  />
                </Route>

                <Route>
                  <Route
                    path={`${process.env.PUBLIC_URL}/components/campaign/campaignview`}
                    element={<CampaignView />}
                  />
                  <Route
                    path={`${process.env.PUBLIC_URL}/components/campaign/campaigncreate`}
                    element={<CampaignCreate />}
                  />
                </Route>

                <Route>
                  <Route
                    path={`${process.env.PUBLIC_URL}/components/Partner/CreatePartner`}
                    element={<PartnerCreateRoute />}
                  />

                  <Route
                    path={`${process.env.PUBLIC_URL}/components/partner/partnerview`}
                    element={<PartnerViewRoute />}
                  />
                </Route>

                <Route>
                  <Route
                    path={`${process.env.PUBLIC_URL}/components/creative/create`}
                    element={<CreativesCreate />}
                  />
                  <Route
                    path={`${process.env.PUBLIC_URL}/components/creative/view`}
                    element={<CreativesView />}
                  />
                </Route>

                <Route>
                  <Route
                    path={`${process.env.PUBLIC_URL}/components/account/accountcreate`}
                    element={<AccountCreate />}
                  />

                  <Route
                    path={`${process.env.PUBLIC_URL}/components/account/accountview`}
                    element={<AccountView />}
                  />
                </Route>

                <Route>
                  <Route
                    path={`${process.env.PUBLIC_URL}/components/translation/view`}
                    element={<Translations />}
                  />
                  <Route
                    path={`${process.env.PUBLIC_URL}/components/flag`}
                    element={<GlobalConfigFlags />}
                  />
                  <Route
                    path={`${process.env.PUBLIC_URL}/components/translator/view`}
                    element={<Translatorcreate />}
                  />

                  <Route>
                    <Route
                      path={`${process.env.PUBLIC_URL}/dashboard`}
                      element={<DashboardRoute />}
                    />
                  </Route>

                  <Route>
                    <Route
                      path={`${process.env.PUBLIC_URL}/impersonate`}
                      element={<ImpersonateRoute />}
                    />
                  </Route>

                  <Route>
                    <Route
                      path={`${process.env.PUBLIC_URL}/faqs`}
                      element={<FAQ />}
                    />
                    <Route
                      path={`${process.env.PUBLIC_URL}/components/faq/create`}
                      element={<FAQCreate />}
                    />
                    <Route
                      path={`${process.env.PUBLIC_URL}/components/faq/view`}
                      element={<FAQView />}
                    />
                  </Route>

                  <Route>
                    <Route
                      path={`${process.env.PUBLIC_URL}/components/reports`}
                      element={<Report />}
                    />
                  </Route>
                  <Route>
                    <Route path={`*`} element={<ReportComponent />} />
                  </Route>
                  {/* <Route>
                    <Route path={`${process.env.PUBLIC_URL}/components/reports/*`} element={<ReportComponent />} />
                  </Route> */}

                  <Route>
                    <Route
                      path={`${process.env.PUBLIC_URL}/components/Partner/CreatePartner`}
                      element={<PartnerCreateRoute />}
                    />

                    <Route
                      path={`${process.env.PUBLIC_URL}/components/partner/partnerview`}
                      element={<PartnerViewRoute />}
                    />
                  </Route>

                  <Route>
                    <Route
                      path={`${process.env.PUBLIC_URL}/components/creative/create`}
                      element={<CreativesCreate />}
                    />
                    <Route
                      path={`${process.env.PUBLIC_URL}/components/creative/view`}
                      element={<CreativesView />}
                    />
                  </Route>

                  <Route>
                    <Route
                      path={`${process.env.PUBLIC_URL}/components/account/accountcreate`}
                      element={<AccountCreate />}
                    />

                    <Route
                      path={`${process.env.PUBLIC_URL}/components/account/accountview`}
                      element={<AccountView />}
                    />
                  </Route>

                  <Route>
                    <Route>
                      <Route
                        path={`${process.env.PUBLIC_URL}/components/Templates/Template`}
                        element={<Template />}
                      />
                      <Route
                        path={`${process.env.PUBLIC_URL}/components/Templates/TemplateDetails`}
                        element={<TemplateDetails />}
                      />
                    </Route>
                    <Route>
                      <Route
                        path={`${process.env.PUBLIC_URL}/components/OceanOnBoarding/OceanOnBoardingView`}
                        element={<OceanOnBoardingView />}
                      />
                      <Route
                        path={`${process.env.PUBLIC_URL}/components/OceanOnBoarding/OceanOnBoardingCreate`}
                        element={<OceanOnBoardingCreate />}
                      />
                    </Route>
                    <Route>
                      <Route
                        path={`${process.env.PUBLIC_URL}/components/ManageUser/ViewUser`}
                        element={<ManageUserView />}
                      />
                      <Route
                        path={`${process.env.PUBLIC_URL}/components/ManageUser/CreateUser`}
                        element={<ManageUserCreate />}
                      />
                    </Route>
                    <Route>
                      <Route
                        path={`${process.env.PUBLIC_URL}/components/Wallpaper/Wallpaper`}
                        element={<Wallpaper />}
                      />
                    </Route>
                    {/* <Route>
                      <Route
                        path={`Wallpaper/*`}
                        element={<ReportComponent />}
                      />
                    </Route>  */}
                  </Route>
                </Route>
              </Route>
            </Routes>
          </React.Suspense>
        </ErrorBoundary>
      </AppProviders>
    </BrowserRouter>
  </Fragment>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
